import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import { Column } from 'components/shared/Layout';
import FeeBreakdownModal from 'components/shared/FeeBreakdownModal';
import OverrideFeesCta from 'components/shared/OverrideFeesCta';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import formatReason from 'utils/formatReason';
import { subtract } from 'utils/calculator';
import LoyaltyPointsInfos from 'components/shared/LoyaltyPointsInfos';
import { journeyShape, userShape } from 'constants/propTypes';
import useSummatedFaresOverrideFareDifferenceMutation from 'components/mutations/useSummatedFaresOverrideFareDifferenceMutation';
import { isTravelAgent } from 'utils/user';
import PricesAndPointsSummary from 'components/shared/PricesAndPointsSummary';
import {
  StyledBody,
  StyleHeaderItems,
  StyleHeaderItem,
  StyledCostRow
} from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.styles';
import SectionHeaderTitle from 'components/ui/BookingPathReviewBookingSummary/SectionHeaderTitle';
import SectionHeaderBar from 'components/ui/BookingPathReviewBookingSummary/SectionHeaderBar';
import {
  ScreenReaderText,
  StyledBreakdownButton,
  StyledBreakdownIcon,
  StyledBreakdownValue
} from 'components/shared/FeeBreakdownModal/FeeBreakdownModal.styles';

import { BoldFormattedCurrency, BorderedRow } from './ExchangeCost.styles';
import { getFeeBreakdown, getFareDifferenceBreakdown } from 'utils/feeBreakdown';
import getWaivePointsDetails from 'utils/getWaivePointsDetails';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';

const ExchangeCost = ({
  oldBookingFare,
  newBookingFare,
  offlineFee,
  flowForwardFee,
  impulseUpgradeFee,
  exchangeFee,
  totalPrice,
  currency,
  bookingReference,
  overrideFees,
  addAdditionalDetails,
  canOverrideFees,
  loyaltyPointsDifference = 0,
  pointsForUpgradeWithPoints = 0,
  extrasDifference,
  pendingCancellationLegs,
  pendingCreationLegs,
  trainMarginDifference,
  user,
  isLoyalty = false,
  hasNonEurostarLegs = false,
  isNonEurostarLegsFareDifferenceOnly = false,
  isNonEurostarLegsExchangeFeeOnly = false
}) => {
  const history = useHistory();
  const [overridePricesSummatedFares, { loading: overridePricesSummatedFaresLoading }] = useSummatedFaresOverrideFareDifferenceMutation();
  const isExchangeNonEurostarSummatedFareOnly =
    pendingCancellationLegs?.length > 0 &&
    pendingCancellationLegs.every(({ isSummatedFare, serviceType }) => isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType.code));

  const hasFareDiff = newBookingFare !== oldBookingFare && !(isNonEurostarLegsFareDifferenceOnly || isExchangeNonEurostarSummatedFareOnly);
  const hasLoyaltyFareDiff = loyaltyPointsDifference > 0;
  const hasOfflineFee = offlineFee > 0;
  const hasExchangeFee = exchangeFee > 0 && !(isNonEurostarLegsExchangeFeeOnly || isExchangeNonEurostarSummatedFareOnly);
  const hasOverrides = hasFareDiff || hasOfflineFee || hasExchangeFee || hasLoyaltyFareDiff;
  const fareDifferenceWithoutAddOns = subtract(newBookingFare, oldBookingFare);

  const exchangeFeeBreakdown = useMemo(
    () => (exchangeFee > 0 ? getFeeBreakdown(pendingCancellationLegs) : []),
    [exchangeFee, pendingCancellationLegs]
  );
  const fareDifferenceBreakdown = useMemo(
    () => (fareDifferenceWithoutAddOns !== 0 ? getFareDifferenceBreakdown(pendingCancellationLegs, pendingCreationLegs) : []),
    [fareDifferenceWithoutAddOns, pendingCancellationLegs, pendingCreationLegs]
  );

  const shouldShowExchangeFeeBreakdown = exchangeFee > 0 && exchangeFeeBreakdown.length > 0;
  const shouldShowFareDiffBreakdown = fareDifferenceWithoutAddOns !== 0 && fareDifferenceBreakdown.length > 0;

  const showWaiveFeesWarning = (pendingCancellationLegs || []).some(
    (leg) => !EUROSTAR_CARRIERS.includes(leg.serviceType?.code) && (leg.fees || []).some((fee) => fee.provisional && fee.price > 0)
  );
  const [isActive, setIsActive] = useState(true);
  const [isExchangeFeeBreakdownOpen, setIsExchangeFeeBreakdownOpen] = useState(false);
  const [isFareDiffBreakdownOpen, setIsFareDiffBreakdownOpen] = useState(false);

  return (
    <>
      <SectionHeaderBar>
        <SectionHeaderTitle titleId="journeySegment.costOfExchange" isActive={isActive} setIsActive={setIsActive} />
        <StyleHeaderItems className="booking-total">
          <FormattedMessage id="summary.total" />
          <StyleHeaderItem data-cy="summary-price">
            <PricesAndPointsSummary
              price={totalPrice}
              currency={currency}
              isLoyalty={isLoyalty}
              points={pointsForUpgradeWithPoints || loyaltyPointsDifference}
            />
          </StyleHeaderItem>
        </StyleHeaderItems>
      </SectionHeaderBar>
      <StyledBody data-cy="cost-of-exchange" hide={!isActive}>
        <StyledCostRow>
          <Column>
            <OverrideFeesCta
              title={<FormattedMessage id="costOfExchange.title" />}
              showLink={canOverrideFees && hasOverrides && !isTravelAgent(user)}
              onSubmit={async (formState) => {
                if ((formState.fareDiff && hasFareDiff) || formState.offlineFee || formState.exchangeFee) {
                  await overrideFees({
                    variables: {
                      bookingReference,
                      currency,
                      reason: formatReason(formState.reasonCode, formState.additionalExplanation, formState.label),
                      fareDiff: formState.fareDiff && hasFareDiff,
                      offlineFee: formState.offlineFee,
                      exchangeFee: formState.exchangeFee
                    }
                  });
                }

                if (formState.fareDiff && hasLoyaltyFareDiff) {
                  const params = new URLSearchParams(history.location.search);

                  const additionalDetails = getWaivePointsDetails(
                    pendingCreationLegs,
                    pendingCancellationLegs,
                    formatReason(formState.reasonCode, formState.additionalExplanation)
                  );

                  if (additionalDetails?.length) {
                    await addAdditionalDetails({
                      variables: {
                        bookingReference,
                        additionalDetails
                      }
                    });
                  }

                  params.set('waivePoints', 'true');

                  history.replace(`${history.location.pathname}?${params.toString()}`);
                }

                if (formState.fareDiff && !hasNonEurostarLegs) {
                  await overridePricesSummatedFares({
                    variables: {
                      pendingCancellationLegs,
                      pendingCreationLegs,
                      reference: bookingReference
                    }
                  });
                }
              }}
              includeReason
              includeFareDiff={hasFareDiff || hasLoyaltyFareDiff}
              includeOfflineFee={hasOfflineFee}
              includeExchangeFee={hasExchangeFee}
              loading={overridePricesSummatedFaresLoading}
              waiveFeesWarning={showWaiveFeesWarning && <FormattedMessage id="overrideFees.waiveFeesWarning" />}
            />
          </Column>
        </StyledCostRow>
        <StyledCostRow data-cy="exchange-cost-diff" data-testid="exchange-cost-diff">
          <Column span={4}>
            <FormattedMessage id="costOfExchange.fareDifference" />
          </Column>
          <Column span={4}>
            <StyledBreakdownValue>
              <FormattedCurrency amount={fareDifferenceWithoutAddOns} currency={currency} />
              {shouldShowFareDiffBreakdown && (
                <StyledBreakdownButton
                  data-testid="fare-diff-breakdown-button"
                  type="button"
                  onClick={() => setIsFareDiffBreakdownOpen(true)}
                >
                  <StyledBreakdownIcon size={16} />
                  <ScreenReaderText>
                    <FormattedMessage id="journeySegment.exchangeFareDifference" />
                  </ScreenReaderText>
                </StyledBreakdownButton>
              )}
            </StyledBreakdownValue>
            {pointsForUpgradeWithPoints > 0 ? (
              <>
                &nbsp;(
                <FormattedCurrency amount={fareDifferenceWithoutAddOns} currency={currency} />
                &nbsp;
                <FormattedMessage
                  data-cy="exchange-cost-points"
                  id="reviewBooking.paidWithPoints"
                  values={{ points: pointsForUpgradeWithPoints }}
                />
                )
              </>
            ) : isLoyalty ? (
              <LoyaltyPointsInfos dataId="points-details" id="searchResults.pts" values={loyaltyPointsDifference} />
            ) : null}
          </Column>
        </StyledCostRow>
        <StyledCostRow data-cy="exchange-cost-fee" data-testid="exchange-cost-fee">
          <Column span={4}>
            <FormattedMessage id="costOfExchange.exchangeFee" />
          </Column>
          <Column span={4}>
            <StyledBreakdownValue>
              <FormattedCurrency amount={exchangeFee} currency={currency} />
              {shouldShowExchangeFeeBreakdown && (
                <StyledBreakdownButton
                  data-testid="exchange-fee-breakdown-button"
                  type="button"
                  onClick={() => setIsExchangeFeeBreakdownOpen(true)}
                >
                  <StyledBreakdownIcon size={16} />
                  <ScreenReaderText>
                    <FormattedMessage id="journeySegment.exchangeFees" />
                  </ScreenReaderText>
                </StyledBreakdownButton>
              )}
            </StyledBreakdownValue>
          </Column>
        </StyledCostRow>
        <StyledCostRow data-cy="exchange-extras-cost-fee" data-testid="exchange-extras-cost-fee">
          <Column span={4}>
            <FormattedMessage id="costOfExchange.exchangeExtrasFee" />
          </Column>
          <Column span={4}>
            <FormattedCurrency amount={extrasDifference} currency={currency} />
          </Column>
        </StyledCostRow>
        {trainMarginDifference > 0 && (
          <StyledCostRow data-cy="train-margin-difference">
            <Column span={4}>
              <FormattedMessage id="costOfExchange.trainsMarginDifference" />
            </Column>
            <Column span={4}>
              <FormattedCurrency amount={trainMarginDifference} currency={currency} />
            </Column>
          </StyledCostRow>
        )}
        {offlineFee > 0 && (
          <StyledCostRow data-cy="exchange-cost-offline" data-testid="exchange-cost-offline">
            <Column span={4}>
              <FormattedMessage id="costOfExchange.offlineFee" />
            </Column>
            <Column span={4}>
              <FormattedCurrency amount={offlineFee} currency={currency} />
            </Column>
          </StyledCostRow>
        )}
        {flowForwardFee > 0 && (
          <StyledCostRow data-cy="exchange-cost-flow-forward" data-testid="exchange-cost-flow-forward">
            <Column span={4}>
              <FormattedMessage id="costOfExchange.flowForwardFee" />
            </Column>
            <Column span={4}>
              <FormattedCurrency amount={flowForwardFee} currency={currency} />
            </Column>
          </StyledCostRow>
        )}
        {impulseUpgradeFee > 0 && (
          <StyledCostRow data-cy="exchange-cost-impulse-upgrade" data-testid="exchange-cost-impulse-upgrade">
            <Column span={4}>
              <FormattedMessage id="costOfExchange.impulseUpgradeFee" />
            </Column>
            <Column span={4}>
              <FormattedCurrency amount={impulseUpgradeFee} currency={currency} />
            </Column>
          </StyledCostRow>
        )}
        <BorderedRow data-cy="exchange-cost-total" data-testid="exchange-cost-total">
          <Column span={4}>
            <FormattedMessage id="costOfExchange.totalPrice" />
          </Column>
          <Column span={4}>
            <BoldFormattedCurrency amount={totalPrice} currency={currency} />
            {pointsForUpgradeWithPoints > 0 ? (
              <>
                &nbsp;(
                <FormattedCurrency amount={fareDifferenceWithoutAddOns} currency={currency} />
                &nbsp;
                <FormattedMessage id="reviewBooking.paidWithPoints" values={{ points: pointsForUpgradeWithPoints }} />)
              </>
            ) : isLoyalty ? (
              <LoyaltyPointsInfos dataId="points-details" id="searchResults.pts" values={loyaltyPointsDifference} />
            ) : null}
          </Column>
        </BorderedRow>
      </StyledBody>
      {shouldShowExchangeFeeBreakdown && (
        <FeeBreakdownModal
          isOpen={isExchangeFeeBreakdownOpen}
          onClose={() => setIsExchangeFeeBreakdownOpen(false)}
          testIdPrefix="exchange-fee"
          titleMessageId="journeySegment.exchangeFees"
          amount={exchangeFee}
          currency={currency}
          carrierMessageId="journeySegment.serviceTypeExchangeFee"
          sections={exchangeFeeBreakdown}
        />
      )}
      {shouldShowFareDiffBreakdown && (
        <FeeBreakdownModal
          isOpen={isFareDiffBreakdownOpen}
          onClose={() => setIsFareDiffBreakdownOpen(false)}
          testIdPrefix="fare-diff"
          titleMessageId="journeySegment.exchangeFareDifference"
          amount={fareDifferenceWithoutAddOns}
          currency={currency}
          carrierMessageId="journeySegment.serviceTypeExchangeFareDifference"
          sections={fareDifferenceBreakdown}
        />
      )}
    </>
  );
};

ExchangeCost.propTypes = {
  oldBookingFare: PropTypes.number.isRequired,
  newBookingFare: PropTypes.number.isRequired,
  exchangeFee: PropTypes.number.isRequired,
  offlineFee: PropTypes.number.isRequired,
  flowForwardFee: PropTypes.number.isRequired,
  impulseUpgradeFee: PropTypes.number,
  totalPrice: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  bookingReference: PropTypes.string.isRequired,
  overrideFees: PropTypes.func,
  addAdditionalDetails: PropTypes.func,
  canOverrideFees: PropTypes.bool,
  loyaltyPointsDifference: PropTypes.number,
  pointsForUpgradeWithPoints: PropTypes.number,
  extrasDifference: PropTypes.number,
  pendingCreationLegs: journeyShape,
  pendingCancellationLegs: journeyShape,
  user: userShape,
  isLoyalty: PropTypes.bool,
  trainMarginDifference: PropTypes.number,
  hasNonEurostarLegs: PropTypes.bool,
  isNonEurostarLegsFareDifferenceOnly: PropTypes.bool,
  isNonEurostarLegsExchangeFeeOnly: PropTypes.bool
};

ExchangeCost.defaultProps = {
  impulseUpgradeFee: 0
};

export default ExchangeCost;
